import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import ContractCard from './ContractCard';
import TermsEditModal from './TermsEditModal';
import TermsDeleteModal from './TermsDeleteModal';

export default function TermContract(props) {
  const {
    storeName,
    terms,
    setTerms,
    provisions,
    setProvisions,
    defaultProvisions
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isClickedEditTerms, setIsClickedEditTerms] = useState(false);
  const [isClickedDeleteTerms, setIsClickedDeleteTerms] = useState(false);

  const [isSavingTerms, setIsSavingTerms] = useState(false);

  const [clickedTerms, setClickedTerms] = useState(undefined);

  const [memberships, setMemberships] = useState([]);
  const [categories, setCategories] = useState([]);

  // 적용 회원권 선택 패널용 데이터 로드 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('membership/', 'GET');

        if (responseData.status === 200) {
          setMemberships(responseData.data.memberShips);
          setCategories(responseData.data.categories);
        }
      }
    )();
  }, [])

  // 카드 클릭시 — 편집 모달 진입 //
  function onClickEditTerms(data) {
    setClickedTerms(data);
    setIsClickedEditTerms(true);
  }

  // 편집 모달 닫기 //
  function onClickExitEditTerms() {
    setClickedTerms(undefined);
    setIsClickedEditTerms(false);
  }

  // 카드 휴지통 클릭시 — 삭제 모달 진입 //
  function onClickDeleteTerms(data) {
    setClickedTerms(data);
    setIsClickedDeleteTerms(true);
  }

  // 삭제 모달 닫기 //
  function onClickExitDeleteTerms() {
    setClickedTerms(undefined);
    setIsClickedDeleteTerms(false);
  }

  // 약관 저장하기 //
  function onClickSaveTerms(data) {
    if (data.title === '') {
      alert(t('myStoreSetting.terms.termsTitleRequired'));
      return;
    }
    else if (data.provisionKeys.length === 0) {
      alert(t('myStoreSetting.terms.termsProvisionRequired'));
      return;
    }
    else if (isSavingTerms) {
      alert(t('myStoreSetting.terms.termsSaving'));
      return;
    }

    setIsSavingTerms(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('terms/save', 'POST', data);

        if (responseData.status === 200) {
          setTerms(responseData.data.terms);
          setProvisions(responseData.data.provisions);
          setIsSavingTerms(false);
          setClickedTerms(undefined);
          setIsClickedEditTerms(false);
        }
        else {
          alert(t('myStoreSetting.terms.termsSaveFailedAlert'));
          setIsSavingTerms(false);
        }
      }
    )();
  }

  // 약관 삭제 실행 //
  function onClickExcuteDeleteTerms() {
    if (isSavingTerms) {
      alert(t('myStoreSetting.terms.deleting'));
      return;
    }

    setIsSavingTerms(true);

    let requestData = {
      id: clickedTerms.id
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('terms/delete', 'POST', requestData);

        if (responseData.status === 200) {
          setTerms(responseData.data.terms);
          setProvisions(responseData.data.provisions);
          setIsSavingTerms(false);
          setClickedTerms(undefined);
          setIsClickedDeleteTerms(false);
        }
        else if (responseData.status === 201) {
          alert(t('myStoreSetting.terms.deleteTermsAlreadyDeleted'));
          setIsSavingTerms(false);
        }
        else if (responseData.status === 202) {
          alert(t('myStoreSetting.terms.deleteTermsUnauthorized'));
          setIsSavingTerms(false);
        }
        else {
          alert(t('myStoreSetting.terms.deleteTermsFailed'));
          setIsSavingTerms(false);
        }
      }
    )();
  }

  // 약관 활성/비활성 토글 — 카드에서 직접 토글, 즉시 백엔드 반영 //
  function onClickTermsStatus(index) {
    if (isSavingTerms) {
      alert(t('myStoreSetting.terms.termsSaving'));
      return;
    }

    let newTerms = terms.slice();

    newTerms[index] = { ...newTerms[index], status: !newTerms[index].status };

    let statuses = newTerms.map((data) => ({ id: data.id, status: data.status }));

    setIsSavingTerms(true);
    setTerms(newTerms);

    let requestData = {
      statuses: statuses
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('terms/state', 'POST', requestData);

        if (responseData.status === 200) {
          setIsSavingTerms(false);
        }
        else {
          alert(t('myStoreSetting.terms.termsSaveFailed'));
          setIsSavingTerms(false);
        }
      }
    )();
  }

  return (
    <TopLevelWrapper>
      {
        isClickedEditTerms &&
        <TermsEditModal
          storeName={storeName}
          clickedTerms={clickedTerms}
          defaultProvisions={defaultProvisions}
          provisions={provisions}
          setProvisions={setProvisions}
          setTerms={setTerms}
          memberships={memberships}
          categories={categories}
          onClickExit={onClickExitEditTerms}
          onClickSave={onClickSaveTerms} />
      }
      {
        isClickedDeleteTerms &&
        <TermsDeleteModal
          onClickExit={onClickExitDeleteTerms}
          onClickExcuteDeleteTerms={onClickExcuteDeleteTerms} />
      }

      <TitleWrapper>
        <PrefixText fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.primary}>{t('myStoreSetting.terms.titlePrefix')}</PrefixText>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('myStoreSetting.terms.title')}</Text>
      </TitleWrapper>

      <CardGridWrapper>
        {
          terms.map((data, index) => (
            <ContractCard
              key={'Contract-' + index}
              data={data}
              index={index}
              onClickCard={onClickEditTerms}
              onClickDelete={onClickDeleteTerms}
              onClickToggleStatus={onClickTermsStatus} />
          ))
        }
        {/* 추가 버튼 */}
        <ContractCard
          key={'Contract-empty'}
          data={undefined}
          onClickCard={onClickEditTerms} />
      </CardGridWrapper>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  margin-top: 30px;

  width: calc(100% - 40px);

  gap: 10px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const CardGridWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  justify-content: flex-start;
  align-items: center;

  padding: 20px;

  width: 100%;

  gap: 15px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const PrefixText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};

  white-space: pre;
`;
